import { useCallback } from "react"

import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { paymentService } from "@/features/payments"
import { useAuthStore } from "@/stores/auth-store"
import { useSubscriptionStore } from "@/stores/subscription-store"
import { useQuizStore } from "@/stores/quiz-store"
import { showErrorToast } from "@/stores/toast-store"
import { auth } from "@/features/auth/providers/firebase"
import { DEV_MOCK_PURCHASE } from "@/config/dev-bypass"

import { requestMyAuraScanReport } from "@/features/aura-scan-report/api/aura-scan-report-service"
import { requestMySoulmateReport } from "@/features/soulmate-report"

import {
  requestMyAstroMapReport,
  requestMyLoveMatchReport,
  requestMyStarReport,
} from "../api/pdf-report-service"
import { getReportPlanCode, isPurchasableReport } from "../config/report-plans"
import type { ReportItem } from "../config/reports-data"
import { useReportPurchaseStore } from "./report-purchase-store"
import { useReportUnlockStore } from "./report-unlock-store"
import { refetchUserReports, useUserReports } from "./use-user-reports"

interface UseReportPurchaseArgs {
  userMainSubscriptionId: string | null
}

/**
 * Charges a report plan against the user's saved payment method (upsell),
 * refreshes subscriptions, and emails the PDF for star reports.
 */
export function useReportPurchase({ userMainSubscriptionId }: UseReportPurchaseArgs) {
  const refreshSubscriptions = useSubscriptionStore((s) => s.refreshSubscriptions)
  const authUser = useAuthStore((s) => s.user)
  const quizEmail = useQuizStore((s) => s.answers.email) as string | undefined

  const purchasingReport = useReportPurchaseStore((s) => s.purchasingReport)
  const error = useReportPurchaseStore((s) => s.error)
  const setPurchasing = useReportPurchaseStore((s) => s.setPurchasing)
  const setSuccess = useReportPurchaseStore((s) => s.setSuccess)
  const setError = useReportPurchaseStore((s) => s.setError)
  const clearError = useReportPurchaseStore((s) => s.clearError)
  const startUnlock = useReportUnlockStore((s) => s.startUnlock)
  const { byType } = useUserReports()

  const purchase = useCallback(
    async (report: ReportItem, planIdOverride?: string): Promise<boolean> => {
      if (purchasingReport) {
        return false
      }

      if (!isPurchasableReport(report.purchaseType)) {
        return false
      }

      // DEV-only mock (toggle: VITE_DEV_MOCK_PURCHASE) — simulate a successful
      // charge so the processing spinner → unlock timer → "Report in Progress"
      // sheet flow can be tested locally without a real subscription or payment.
      // Dead-code-eliminated from production builds. See the "Reports purchase —
      // dev mock" card in Documentation.
      if (DEV_MOCK_PURCHASE) {
        clearError()
        setPurchasing(report)
        await new Promise((resolve) => setTimeout(resolve, 1500))
        startUnlock(report.id)
        setSuccess(report)
        return true
      }

      if (!userMainSubscriptionId) {
        setError("No active subscription to charge. Please try again later.")
        return false
      }

      const email = authUser?.email || quizEmail || ""
      if (!email) {
        setError("Email is required to complete the purchase.")
        return false
      }

      // Modal offers pass an explicit plan; otherwise the backend report list is
      // the source of truth, with the client catalog as fallback.
      const planCode =
        planIdOverride || byType(report.id)?.planId || getReportPlanCode(report.purchaseType)

      clearError()
      setPurchasing(report)

      trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
        productId: planCode,
        price: report.price,
        currency: "USD",
        metadata: { kind: "report", report: report.title },
      })

      try {
        const result = await paymentService.createUpsellSubscription({
          planId: planCode,
          email,
          subscriptionId: userMainSubscriptionId,
        })

        const verifyId = authUser?.appleToken ?? authUser?.uid
        if (verifyId) {
          await refreshSubscriptions(verifyId)
        }

        if (report.purchaseType === "star") {
          const firebaseUser = auth.currentUser
          if (firebaseUser) {
            try {
              const token = await firebaseUser.getIdToken()
              await requestMyStarReport({ subscriptionId: result.id, token })
              // Pull the freshly-created job so the row shows `processing` (with
              // the server's estimatedReadyAt) instead of relying on the local timer.
              await refetchUserReports()
            } catch (pdfError) {
              console.error("star report request failed:", pdfError)
            }
          }
        }

        if (report.purchaseType === "truths") {
          const firebaseUser = auth.currentUser
          if (firebaseUser) {
            try {
              const token = await firebaseUser.getIdToken()
              await requestMyLoveMatchReport({ subscriptionId: result.id, token })
              // Love Match is static and ready at once — pull its ready url/status
              // so it drops out of the cosmetic timer immediately.
              await refetchUserReports()
            } catch (loveError) {
              console.error("love-match report request failed:", loveError)
            }
          }
        }

        if (report.purchaseType === "soulmate") {
          const firebaseUser = auth.currentUser
          if (firebaseUser) {
            try {
              const token = await firebaseUser.getIdToken()
              await requestMySoulmateReport({ subscriptionId: result.id, token })
              // Pull the freshly-created job so the row shows `processing` (the
              // backend generates the report asynchronously).
              await refetchUserReports()
            } catch (soulmateError) {
              console.error("soulmate report request failed:", soulmateError)
            }
          }
        }

        if (report.purchaseType === "astromap") {
          const firebaseUser = auth.currentUser
          if (firebaseUser) {
            try {
              const token = await firebaseUser.getIdToken()
              await requestMyAstroMapReport({ subscriptionId: result.id, token })
              // Astro Map is a static per-zodiac PDF, ready at once — pull its
              // ready url/status so it drops out of the cosmetic timer immediately.
              await refetchUserReports()
            } catch (astroMapError) {
              console.error("astro-map report request failed:", astroMapError)
            }
          }
        }

        if (report.purchaseType === "aura") {
          const firebaseUser = auth.currentUser
          if (firebaseUser) {
            try {
              const token = await firebaseUser.getIdToken()
              await requestMyAuraScanReport({ subscriptionId: result.id, token })
              await refetchUserReports()
            } catch (auraError) {
              console.error("aura-scan report request failed:", auraError)
            }
          }
        }

        trackEvent(ANALYTICS_EVENTS.PURCHASE, {
          productId: planCode,
          transactionId: result.id,
          price: report.price,
          currency: "USD",
          metadata: { kind: "report", report: report.title },
        })

        // Start the unlock countdown, then swap the spinner for the success sheet.
        startUnlock(report.id)
        setSuccess(report)
        return true
      } catch (err) {
        const message = err instanceof Error ? err.message : "An unexpected error occurred"
        // Hide the spinner (returns the user to the reports list) and surface the
        // failure as the global 4s error toast.
        setError(message)
        showErrorToast("Payment error", "Please retry or check your balance")
        trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, {
          productId: planCode,
          metadata: { kind: "report", report: report.title, error: message },
        })
        return false
      }
    },
    [
      authUser,
      byType,
      purchasingReport,
      quizEmail,
      refreshSubscriptions,
      userMainSubscriptionId,
      clearError,
      setPurchasing,
      setSuccess,
      setError,
      startUnlock,
    ],
  )

  const isReportPurchasing = useCallback(
    (report: ReportItem) => purchasingReport?.id === report.id,
    [purchasingReport],
  )

  return {
    purchase,
    isReportPurchasing,
    error,
    clearError,
  }
}
